/** 财务扫描适配层：处理后的 PDF 与每一份原件分别保存；不改变全站普通上传。
 * 只有员工确认过的扫描 File 才携带 documentScan，私有审批仍走自己的归档接口。
 */
import { uploadChunked } from "./upload";
import { authToken } from "./api";

export const SCAN_ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf";
export const isScannable = file => /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(file?.name || "");
export const isScannableDocument = isScannable;
export const scanSize = size => size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(size / 1024))} KB`;
const uploads = new WeakMap();

/** 分片先存全部原件，再存扫描件并建立不可覆盖的版本关系；重试复用已完成文件。
 * skipCompress + shrink:false 保证 HEIC/EXIF 等原始字节不被转码或缩图。
 */
export async function uploadScannedDocument(api, file, opts = {}) {
  const scan = file?.documentScan;
  if (!scan?.originals?.length) return uploadChunked(api, file, opts);
  const owner = authToken();
  let state = uploads.get(file);
  if (!state || state.owner !== owner) {
    state = { owner, originals: [], scanned: null, linked: false };
    uploads.set(file, state);
  }
  const total = file.size + scan.originals.reduce((n, original) => n + original.size, 0);
  let completed = 0;
  const progress = value => {
    const loaded = Math.min(total, completed + (value.loaded || 0));
    const pct = total ? Math.round(loaded / total * 100) : 0;
    opts.onPct?.(pct);
    opts.onProgress?.({ ...value, total, loaded, pct });
  };
  for (let i = 0; i < scan.originals.length; i += 1) {
    if (!state.originals[i]) state.originals[i] = await uploadChunked(api, scan.originals[i], {
      ...opts, shrink: false, skipCompress: true, keepOriginal: false, onPct: undefined, onProgress: progress,
    });
    completed += scan.originals[i].size;
  }
  if (!state.scanned) state.scanned = await uploadChunked(api, file, {
    ...opts, shrink: false, skipCompress: true, keepOriginal: false, onPct: undefined, onProgress: progress,
  });
  if (!state.linked) {
    await api.post(`/files/${state.scanned.fileId}/document-scan`, {
      originalFileIds: state.originals.map(original => original.fileId), manifest: scan.manifest,
    }, { signal: opts.signal });
    state.linked = true;
  }
  opts.onPct?.(100);
  opts.onProgress?.({ pct: 100, loaded: total, total, state: "complete" });
  return state.scanned;
}

/** 单凭证 FormData 附加全部原件；服务器只创建一个付款凭证，不重复核销/通知。 */
export function appendScannedOriginals(form, file) {
  if (!file?.documentScan) return;
  file.documentScan.originals.forEach(original => form.append("originals", original));
  form.append("scanManifest", JSON.stringify(file.documentScan.manifest));
}

/** 只取已核对的、明确识别的字段；缺值/不确定值不能变成金额0或猜测的公司名。 */
export function reviewedScanFields(recognition) {
  if (!recognition?.applyFields) return {};
  const uncertain = new Set(recognition.uncertainFields || []);
  return Object.fromEntries(Object.entries(recognition.fields || {}).filter(([key, value]) =>
    !uncertain.has(key) && value !== null && value !== undefined && String(value).trim() !== ""));
}
